import React, { useEffect, useState } from "react";
import Lenis from "lenis";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import TeamHero from "../components/team/TeamHero";
import TeamIntro from "../components/team/TeamIntro";
import TeamGrid from "../components/team/TeamGrid";
import TeamGame from "../components/team/TeamGame";
import TeamValues from "../components/team/TeamValues";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CustomCursor from "../components/CustomCursor";
import Loader from "../components/Loader";
import ScrollToTop from "../components/ScrollToTop";
import SEO from "../components/SEO";
import SectionStepper from "../components/SectionStepper";
import "../styles/Team.css";

const Team = () => {
  const [loading, setLoading] = useState(true);

  // Smooth scroll with Lenis
  useEffect(() => {
    if (loading) return;
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.4
    });

    let raf;
    const tick = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    window.lenis = lenis;
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.lenis = null;
    };
  }, [loading]);

  return (
    <>
      <SEO 
        title="Our Collective" 
        description="Meet the visionaries, engineers, and cinematic artists behind Meraki Solutions." 
        keywords="Meraki Solutions, Team, Collective, Masters, Visionaries, Cinematic Studio, Digital Engineering" 
        image="/images/team/team_hero.jpg" 
        url="https://meraki-solutions.in/team" 
        type="website" 
      />
      {/* JSON‑LD Structured Data for the Team collective */}
      <Helmet>
        <script type="application/ld+json">
        {`{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Meraki Solutions",
          "url": "https://meraki-solutions.in",
          "logo": "https://meraki-solutions.in/meraki_solutions_logo.png",
          "sameAs": [
            "https://twitter.com/merakisolutions",
            "https://www.linkedin.com/company/meraki-solutions"
          ],
          "department": [
            {
              "@type": "Person",
              "name": "Chitra Katyal",
              "jobTitle": "Founder & Creative Head",
              "url": "https://meraki-solutions.in/team#chitra"
            },
            {
              "@type": "Person",
              "name": "Sarah Chen",
              "jobTitle": "Technical Architect",
              "url": "https://meraki-solutions.in/team#sarah"
            }
          ]
        }`}
        </script>
      </Helmet>

      <Loader onDone={() => setLoading(false)} />
      {/* <CustomCursor /> */}
      <div className="grain" />
      <div className="team-page-wrapper">
        <Navbar />
        <main>
          <SectionStepper 
            sections={[
              { id: "top", label: "Hero" },
              { id: "introduction", label: "Manifesto" },
              { id: "collective", label: "Collective" },
              { id: "ethos", label: "Ethos" },
              { id: "synergy", label: "Synergy" }
            ]} 
          />
          <TeamHero />
          <TeamIntro 
            title="The Human Element"
            subHeading="Architecting the Future of Digital Expression"
            paragraph="At Meraki Solutions, we don't just build products; we cultivate talent. Our collective of designers, engineers, and strategists are united by a singular obsession: creating work that feels alive. We are a boutique studio where every member is a master of their craft, dedicated to pushing the boundaries of what's possible in the digital realm."
          />
          <TeamGrid />
          <TeamValues />
          <TeamGame />
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
};

export default Team;
